import type {
  GroupAnalysisData,
  GroupAnalysisType,
} from '@/generated/types/navigator/types.gen';
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useIntersection } from '@mantine/hooks';
import type { BoundingBoxString, Summary } from '@/features/types';
import {
  getGoogleEventForAnalysis,
  GoogleEvents,
} from '@/features/googleEvents';
import type { ListDataProps } from '@/features/analysisApi';
import { analysisApi } from '@/features/analysisApi';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import TagManager from 'react-gtm-module';
import classes from './GroupListComponent.module.css';
import { ListItems } from './ListItems';
import {
  DESKTOP_VIEWBOX_PADDING,
  MOBILE_VIEWBOX_PADDING,
  updateViewport,
} from '@/views/map/deckglUtils';
import { useWindowDimensions } from '@/hooks/useWindowDimension';
import { HelpLink } from '@/components/Link/HelpLink';
import { analysisView } from '@/app/analysis';
import { WKTBBoxToArray } from '@/helpers/stringConversion';
import type { WebMercatorViewportOptions } from '@/views/map/types';

import { ClearFilter } from '@/views/list/ClearFilter';
import { NoResultsForSelectedAnalysis } from '@/views/list/NoResultsForSelectedAnalysis';

interface Props {
  setShowExportModal: (e: boolean) => void;
  listRef: React.RefObject<HTMLDivElement | null>;
  onClick: (
    callback: (v: WebMercatorViewportOptions) => WebMercatorViewportOptions,
    groupName: string
  ) => void;
  activeGroupName?: string | null;
  selectedNetworkGroup?: string | null;
  dataProps: ListDataProps;
}

export const GroupListComponent = ({ listRef, ...props }: Props) => {
  const dispatch = useAppDispatch();
  const intl = useClientTerminology();
  const query = useAppSelector((state) => state.filterQuery);
  const [summary, setSummary] = useState<Summary>();
  const [items, setItems] = useState<GroupAnalysisData[]>([]);
  const { selectedAnalysis, selectedNetworkGroup, activeGroupName } =
    useAppSelector((s) => s.analysisMap);
  const { width } = useWindowDimensions();
  const { isLoading, isFetching, data, originalArgs, limit, page, setPage } =
    props.dataProps;

  const { statusChanged, worklogItem, worklogItems } = useAppSelector(
    (s) => s.worklogMap
  );
  const isInitialLoad = (isLoading || isFetching) && page === 1;

  const { ref: sentinelRef, entry } = useIntersection({
    root: listRef.current,
    threshold: 1,
  });
  const isScrolled = entry ? !entry.isIntersecting : false;

  const isFilteredToEmpty = useMemo(
    () => summary?.totalLength === 0 && query.isSelected,
    [summary?.totalLength, query.isSelected]
  );

  const noResultsForSelectedAnalysis = useMemo(
    () => summary?.total === 0 && !isFilteredToEmpty,
    [summary?.total, isFilteredToEmpty]
  );

  if (isFilteredToEmpty) {
    TagManager.dataLayer({
      dataLayer: {
        event: getGoogleEventForAnalysis(
          selectedAnalysis,
          GoogleEvents.noResultsFilter,
          true
        ),
        analysisFilters: query,
      },
    });
  }

  useEffect(() => {
    if (statusChanged) {
      dispatch(analysisApi.util.invalidateTags([{ type: 'itemData' }]));
    }
  }, [statusChanged, dispatch]);

  useEffect(() => {
    setItems((prev) => {
      return prev.map((item) => {
        if (item.group === worklogItem.level3) {
          return {
            ...item,
            workStatus: worklogItem.status,
          };
        }
        return item;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusChanged, worklogItems]);

  const hasMoreToLoad = useCallback(() => {
    return summary && summary.total > 0 && items.length < summary.total;
  }, [items.length, summary]);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastItemRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading || isFetching) {
        return;
      }

      if (observer.current) {
        observer.current.disconnect();
      }
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMoreToLoad()) {
          setPage((prevPage: number) => prevPage + 1);
        }
      });
      if (node) {
        observer.current.observe(node);
      }
    },
    [isLoading, isFetching, hasMoreToLoad, setPage]
  );

  const handleGroupItemClick = (
    bbox: BoundingBoxString | undefined,
    groupName?: string
  ) => {
    if (!groupName) {
      // clicking the no group group
      return;
    }

    props.onClick((v: WebMercatorViewportOptions) => {
      return updateViewport(
        v, // current viewState
        WKTBBoxToArray(bbox),
        width, // current width
        DESKTOP_VIEWBOX_PADDING,
        MOBILE_VIEWBOX_PADDING
      );
    }, groupName);
  };

  useEffect(() => {
    if (data && data.data) {
      const results = data.data;
      const queryPage =
        originalArgs && typeof originalArgs === 'object'
          ? originalArgs.page
          : undefined;
      if (page === 1) {
        setItems(results);
        setSummary(data.summary);
      } else if (page === queryPage) {
        setItems((prev) => {
          if (prev.length + results.length < page * limit) {
            // only append if it's new data
            return [...prev, ...results];
          }
          return prev;
        });
      }
    }
  }, [data, originalArgs, page, limit, setPage]);

  useEffect(() => {
    setPage(1);
  }, [query, setPage, selectedNetworkGroup, selectedAnalysis, activeGroupName]);

  const helpLink =
    analysisView[selectedAnalysis as GroupAnalysisType].listHelpLink;

  return (
    <>
      {isFilteredToEmpty && (
        <div className={`${classes.heading}`}>
          <ClearFilter />
        </div>
      )}
      {!isInitialLoad && noResultsForSelectedAnalysis && (
        <div className={classes.heading}>
          <NoResultsForSelectedAnalysis />
        </div>
      )}
      <div
        className={classes.main}
        ref={listRef}
        id={`${selectedAnalysis}-list`}
        data-testid="analysis-list"
        data-scrolled={isScrolled || undefined}
        style={{
          transition: 'box-shadow 0.2s ease',
          boxShadow: isScrolled
            ? 'inset 0 8px 8px -8px rgba(0, 0, 0, 0.12)'
            : undefined,
        }}
      >
        {/* 1px sentinel — when it scrolls out of view, isScrolled becomes true */}
        <div ref={sentinelRef} style={{ height: 1 }} />
        {isInitialLoad && <div className={classes.loading}>Loading...</div>}
        {!isInitialLoad && !noResultsForSelectedAnalysis && (
          <ListItems
            items={items}
            lastItemRef={lastItemRef}
            onClick={handleGroupItemClick}
          />
        )}
      </div>
      {helpLink && (
        <div className={classes.footer} data-testid="list-footer">
          <HelpLink
            text={intl.formatMessage({ id: 'list.learnMore' })}
            link={helpLink}
            textSize="sm"
          ></HelpLink>
          {intl.formatMessage({ id: 'list.aboutHowRiskIsAggregated' })}
        </div>
      )}
    </>
  );
};
